import { useMemo } from 'react';

import {
  Box,
  Chip,
  Stack,
  Typography,
} from '@mui/material';

import type {
  MarginCenterComparisonMode,
  MarginCenterCustomerProductActivityFactsResponse,
  MarginCenterCustomerProductActivitySelection,
} from '../types/marginCenter';


type ComponentDriver =
  | 'frequency_led'
  | 'size_led'
  | 'combined_decline'
  | 'offsetting_components'
  | 'undetermined';

type BridgeReading = {
  referenceRate: number;
  currentRate: number;
  delta: number;
  deltaPct: number;
  frequencyEffect: number;
  sizeEffect: number;
  driver: ComponentDriver;
  currentEvents: number;
  referenceEvents: number;
  periodDays: number;
  referenceDays: number;
};

const numberFormat = new Intl.NumberFormat('es-ES', {
  maximumFractionDigits: 2,
});

function display(value: number): string {
  if (!Number.isFinite(value)) return '—';
  // Evita mostrar un -0 en contribuciones muy pequeñas.
  return numberFormat.format(Math.abs(value) < 0.005 ? 0 : value);
}

function signed(value: number): string {
  return `${value > 0.005 ? '+' : ''}${display(value)}`;
}

function isPositiveFinite(value: number | null | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

function classifyDriver(frequencyEffect: number, sizeEffect: number, delta: number): ComponentDriver {
  const tolerance = Math.max(1e-9, Math.abs(delta) * 1e-10);
  const frequencyNegative = frequencyEffect < -tolerance;
  const sizeNegative = sizeEffect < -tolerance;
  const frequencyPositive = frequencyEffect > tolerance;
  const sizePositive = sizeEffect > tolerance;

  if ((frequencyNegative && sizePositive) || (sizeNegative && frequencyPositive)) {
    return 'offsetting_components';
  }
  if (!frequencyNegative && !sizeNegative) return 'undetermined';

  const negativeTotal = Math.max(0, -frequencyEffect) + Math.max(0, -sizeEffect);
  if (negativeTotal <= tolerance) return 'undetermined';
  if (Math.max(0, -frequencyEffect) / negativeTotal >= 0.7) return 'frequency_led';
  if (Math.max(0, -sizeEffect) / negativeTotal >= 0.7) return 'size_led';
  return 'combined_decline';
}

const driverText: Record<ComponentDriver, string> = {
  frequency_led: 'El cambio se concentra en menos días de compra.',
  size_led: 'El cambio se concentra en menos unidades por día con compra.',
  combined_decline: 'Se combinan menos días de compra y menor tamaño medio.',
  offsetting_components: 'Un componente empeora mientras el otro compensa parte de la caída.',
  undetermined: 'No hay un componente predominante interpretable.',
};

function computeReading(
  facts: MarginCenterCustomerProductActivityFactsResponse,
  selection: MarginCenterCustomerProductActivitySelection,
): BridgeReading | null {
  const current = facts.current;
  const reference = facts.comparison;
  if (!current || !reference) return null;

  const currentDays = current.period_days ?? selection.period_days;
  const referenceDays = reference.period_days ?? selection.comparison_period_days;
  const currentEvents = current.purchase_days;
  const referenceEvents = reference.purchase_days;

  if (
    !isPositiveFinite(currentDays)
    || !isPositiveFinite(referenceDays)
    || !isPositiveFinite(currentEvents)
    || !isPositiveFinite(referenceEvents)
    || !isPositiveFinite(current.units)
    || !isPositiveFinite(reference.units)
  ) return null;

  const frequencyCurrent = 30 * currentEvents / currentDays;
  const frequencyReference = 30 * referenceEvents / referenceDays;
  const sizeCurrent = current.units / currentEvents;
  const sizeReference = reference.units / referenceEvents;
  const currentRate = frequencyCurrent * sizeCurrent;
  const referenceRate = frequencyReference * sizeReference;
  const delta = currentRate - referenceRate;

  if (!Number.isFinite(delta) || referenceRate <= 0 || delta >= -1e-9) {
    return null;
  }

  // Descomposición simétrica sobre operandos sin redondeos intermedios
  // en este componente. La RPC de origen entrega cantidades redondeadas
  // a dos decimales: no es precisión transaccional ilimitada.
  const frequencyEffect =
    (frequencyCurrent - frequencyReference) * (sizeCurrent + sizeReference) / 2;
  const sizeEffect =
    (sizeCurrent - sizeReference) * (frequencyCurrent + frequencyReference) / 2;

  if (![frequencyEffect, sizeEffect, currentRate, referenceRate]
    .every(Number.isFinite)) return null;

  return {
    referenceRate,
    currentRate,
    delta,
    deltaPct: (delta / referenceRate) * 100,
    frequencyEffect,
    sizeEffect,
    driver: classifyDriver(frequencyEffect, sizeEffect, delta),
    currentEvents,
    referenceEvents,
    periodDays: currentDays,
    referenceDays,
  };
}

function Contribution({
  label,
  value,
  maxMagnitude,
}: {
  label: string;
  value: number;
  maxMagnitude: number;
}) {
  const share = maxMagnitude > 0 ? Math.abs(value) / maxMagnitude * 100 : 0;
  const tone = value < -1e-9 ? '#fb7185' : value > 1e-9 ? '#34d399' : '#94a3b8';
  return (
    <Box sx={{ minWidth: 0 }}>
      <Stack direction="row" justifyContent="space-between" spacing={1}>
        <Typography variant="body2" sx={{ color: '#cbd5e1', fontWeight: 700 }}>
          {label}
        </Typography>
        <Typography variant="body2" sx={{ color: tone, fontWeight: 850, fontVariantNumeric: 'tabular-nums' }}>
          {signed(value)} uds./30 días
        </Typography>
      </Stack>
      <Box sx={{ mt: 0.55, height: 5, bgcolor: 'rgba(148,163,184,0.12)', borderRadius: 5, overflow: 'hidden' }}>
        <Box sx={{ height: '100%', width: `${Math.min(100, Math.max(0, share))}%`, bgcolor: tone, borderRadius: 5 }} />
      </Box>
    </Box>
  );
}

export default function CustomerRelationshipConsumptionBridge({
  facts,
  selection,
  comparisonMode,
  lastAvailableDate,
}: {
  facts: MarginCenterCustomerProductActivityFactsResponse;
  selection: MarginCenterCustomerProductActivitySelection | null;
  comparisonMode: MarginCenterComparisonMode;
  lastAvailableDate: string | null;
}) {
  const reading = useMemo(
    () => selection ? computeReading(facts, selection) : null,
    [facts, selection],
  );

  if (comparisonMode === 'none' || !selection) return null;

  // Un periodo que continúa después de la última fecha transaccional
  // observable de la cuenta NO puede atribuir sus días sin datos a consumo.
  const beyondCutoff =
    !lastAvailableDate || !selection.end_date || selection.end_date > lastAvailableDate;
  const overlaps = Boolean(
    selection.start_date && selection.end_date
    && selection.comparison_start_date && selection.comparison_end_date
    && selection.start_date <= selection.comparison_end_date
    && selection.comparison_start_date <= selection.end_date,
  );
  const noCurrentPurchases = facts.current?.purchase_days === 0;
  const shortWindow = Boolean(
    selection.period_days < 30
    || (selection.comparison_period_days !== null && selection.comparison_period_days < 30),
  );

  if (beyondCutoff || overlaps || noCurrentPurchases || !reading) {
    let reason = 'La comparación seleccionada no ofrece un descenso de consumo descomponible con unidades y compras positivas en ambos periodos.';
    if (beyondCutoff) {
      reason = 'La selección se extiende más allá de la última fecha de datos disponible en la cuenta. No interpretamos ese tramo sin datos como un descenso de consumo.';
    } else if (overlaps) {
      reason = 'Los periodos seleccionados se solapan. El desglose requiere dos observaciones temporales independientes.';
    } else if (noCurrentPurchases) {
      reason = 'No hay compras en el periodo actual. La ausencia de compra se investiga primero mediante Continuidad y Cadencia.';
    }
    return (
      <Box sx={{ mt: 1.15, p: 1.25, borderRadius: 1.7, bgcolor: 'rgba(15,23,42,0.36)', border: '1px solid rgba(148,163,184,0.15)' }}>
        <Typography variant="subtitle2" sx={{ color: '#cbd5e1', fontWeight: 800 }}>
          Explicación del cambio de consumo
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.45, color: '#94a3b8' }}>
          {reason}
        </Typography>
      </Box>
    );
  }

  const maxMagnitude = Math.max(
    Math.abs(reading.frequencyEffect),
    Math.abs(reading.sizeEffect),
    1e-9,
  );
  const limitedSupport = reading.currentEvents < 3 || reading.referenceEvents < 3 || shortWindow;

  return (
    <Box sx={{ mt: 1.25, p: { xs: 1.35, sm: 1.55 }, borderRadius: 2, bgcolor: 'rgba(14,25,43,0.64)', border: '1px solid rgba(94,234,212,0.19)' }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={0.9}>
        <Box>
          <Typography variant="overline" sx={{ color: '#5eead4', fontWeight: 850, lineHeight: 1.3 }}>
            Inteligencia explicativa · relación cliente-producto
          </Typography>
          <Typography variant="subtitle2" sx={{ color: '#f8fafc', fontWeight: 850, mt: 0.15 }}>
            {driverText[reading.driver]}
          </Typography>
        </Box>
        <Chip
          size="small"
          label={limitedSupport ? 'Lectura con soporte limitado' : 'Descomposición descriptiva'}
          sx={{ color: '#99f6e4', bgcolor: 'rgba(20,184,166,0.10)', border: '1px solid rgba(45,212,191,0.25)', fontWeight: 750 }}
        />
      </Stack>

      <Typography variant="body2" sx={{ color: '#cbd5e1', mt: 1 }}>
        Ritmo observado: <strong>{display(reading.referenceRate)}</strong> → <strong>{display(reading.currentRate)}</strong> unidades cada 30 días.
        {' '}Variación de <strong>{signed(reading.delta)} unidades/30 días ({signed(reading.deltaPct)} %)</strong>.
      </Typography>

      <Stack spacing={1.15} sx={{ mt: 1.25 }}>
        <Contribution label="Efecto matemático · frecuencia" value={reading.frequencyEffect} maxMagnitude={maxMagnitude} />
        <Contribution label="Efecto matemático · tamaño de compra" value={reading.sizeEffect} maxMagnitude={maxMagnitude} />
      </Stack>

      <Box sx={{ mt: 1.15, pt: 0.95, borderTop: '1px solid rgba(148,163,184,0.12)' }}>
        <Typography variant="caption" sx={{ color: '#cbd5e1', display: 'block' }}>
          Comprobación: {signed(reading.frequencyEffect)} + {signed(reading.sizeEffect)} = {signed(reading.delta)} uds./30 días.
        </Typography>
        <Typography variant="caption" sx={{ mt: 0.25, display: 'block', color: '#94a3b8' }}>
          Evidencia: {display(reading.referenceEvents)} días de compra comparados y {display(reading.currentEvents)} actuales; ventanas de {display(reading.referenceDays)} y {display(reading.periodDays)} días naturales, normalizadas a 30.
        </Typography>
        {limitedSupport && (
          <Typography variant="caption" sx={{ mt: 0.5, display: 'block', color: '#fcd34d' }}>
            La muestra o duración es reducida. Esta lectura no establece un patrón histórico ni habilita un aviso automático.
          </Typography>
        )}
        <Typography variant="caption" sx={{ mt: 0.5, display: 'block', color: '#64748b', lineHeight: 1.5 }}>
          Los efectos son una descomposición aritmética de dos periodos seleccionados, no causas comerciales. Las barras muestran magnitudes, no probabilidades. La agregación no permite descartar devoluciones, ajustes ni unidades heterogéneas: revise transacciones antes de atribuir el patrón. No evalúa persistencia, P25 independiente, estacionalidad ni episodios; no representa ingresos perdidos ni modifica decisiones.
        </Typography>
      </Box>
    </Box>
  );
}
